import { Deal } from "@/types/deal";
import { RampSettings } from "@/types/ramp";

export interface RampCalculationResult {
  annualDepreciation: number;
  inflationIndex: number[];
  toplineIndex: number[];
  costRampMultipliers: number[];
  revenueRampMultipliers: number[];
}

export interface IndexCalculation {
  years: number;
  inflationIndex: number[];
  toplineIndex: number[];
}

export interface DepreciationCalculation {
  annualDepreciation: number;
  depreciationAmount: number;
  capexAmount: number;
  depreciationRate: number;
}

/**
 * Рассчитывает годовую амортизацию на основе настроек
 */
export function calculateAnnualDepreciation(
  deal: Deal,
  rampSettings: RampSettings
): DepreciationCalculation {
  const grandTotalCapexExVat = deal.budget?.grandTotal || 0;
  const depreciationRate = rampSettings.depreciationPctOfCapex / 100;
  const annualDepreciation = depreciationRate * grandTotalCapexExVat;

  return {
    annualDepreciation,
    depreciationAmount: annualDepreciation,
    capexAmount: grandTotalCapexExVat,
    depreciationRate,
  };
}

/**
 * Рассчитывает индексы инфляции и роста выручки на N лет
 */
export function calculateIndexes(
  rampSettings: RampSettings,
  years: number = 10
): IndexCalculation {
  const inflationIndex: number[] = [100];
  const toplineIndex: number[] = [100];

  const inflationMultiplier = 1 + rampSettings.inflationPct / 100;
  const toplineMultiplier = 1 + rampSettings.toplineGrowthPct / 100;

  for (let year = 2; year <= years; year++) {
    const prevInflation = inflationIndex[inflationIndex.length - 1];
    const newInflation = prevInflation * inflationMultiplier;
    inflationIndex.push(Number(newInflation.toFixed(2)));

    const prevTopline = toplineIndex[toplineIndex.length - 1];
    const newTopline = prevTopline * toplineMultiplier;
    toplineIndex.push(Number(newTopline.toFixed(2)));
  }

  return {
    years,
    inflationIndex,
    toplineIndex,
  };
}

/**
 * Рассчитывает множители ramp для указанного типа (стоимость или выручка)
 */
export function calculateRampMultipliers(
  rampValues: number[], // [0.7, 0.8, 0.9, 1.0]
  asPercentage: boolean = true
): number[] {
  if (asPercentage) {
    return rampValues.map((val) => Math.round(val * 100));
  }
  return rampValues;
}

/**
 * Проверяет, соответствует ли текущий ramp preset'у
 */
export function getCurrentPreset(
  currentRamp: number[],
  presets: Record<string, number[]>
): string {
  for (const [key, preset] of Object.entries(presets)) {
    if (JSON.stringify(preset) === JSON.stringify(currentRamp)) {
      return key;
    }
  }
  return "custom";
}

/**
 * Рассчитывает все основные метрики для отображения
 */
export function calculateAllRampMetrics(
  deal: Deal,
  rampSettings: RampSettings
): RampCalculationResult {
  const depreciation = calculateAnnualDepreciation(deal, rampSettings);
  const indexes = calculateIndexes(rampSettings);

  return {
    annualDepreciation: depreciation.annualDepreciation,
    inflationIndex: indexes.inflationIndex,
    toplineIndex: indexes.toplineIndex,
    costRampMultipliers: calculateRampMultipliers(rampSettings.costRamp),
    revenueRampMultipliers: calculateRampMultipliers(rampSettings.revenueRamp),
  };
}

/**
 * Проверяет, есть ли изменения в настройках
 */
export function hasChanges(
  original: RampSettings | null,
  current: RampSettings | null
): boolean {
  if (!original || !current) return false;

  return JSON.stringify(original) !== JSON.stringify(current);
}

/**
 * Получает цвета для ramp карточек в зависимости от типа
 */
export function getRampCardColors(type: "revenue" | "cost"): {
  activeBg: string;
  activeText: string;
  inactiveBg: string;
  inactiveText: string;
} {
  if (type === "revenue") {
    return {
      activeBg: "bg-green-100",
      activeText: "text-green-700",
      inactiveBg: "bg-green-50",
      inactiveText: "text-green-600",
    };
  }

  return {
    activeBg: "bg-orange-100",
    activeText: "text-orange-700",
    inactiveBg: "bg-orange-50",
    inactiveText: "text-orange-600",
  };
}

/**
 * Форматирует ramp значения для отображения
 */
export function formatRampForDisplay(ramp: number[]): string {
  return ramp
    .map((val, i) => `Y${i + 1}: ${Math.round(val * 100)}%`)
    .join(", ");
}

/**
 * Рассчитывает сводку выбранных настроек
 */
export function getSettingsSummary(rampSettings: RampSettings): {
  revenueRamp: string;
  costRamp: string;
  toplineGrowth: string;
  inflation: string;
} {
  return {
    revenueRamp: formatRampForDisplay(rampSettings.revenueRamp),
    costRamp: formatRampForDisplay(rampSettings.costRamp),
    toplineGrowth: `${rampSettings.toplineGrowthPct}% annually`,
    inflation: `${rampSettings.inflationPct}% annually`,
  };
}
